import React, {useMemo, useState} from 'react';

import {TruncationType} from '../../components/common/TruncateText/TruncateTextTypes';
import {KeyInfoQueryContextProvider} from '../../components/MultiRunWorkspace/KeyInfoQueryContext';
import {useDetectPanelVariant} from '../../components/Panel/hooks';
import {
  EmbeddablePanel,
  FullPagePanel,
} from '../../components/Panel/StandalonePanel';
import {AutoSyncPanelBankContextProvider} from '../../components/PanelBank/AutoSyncPanelBankContext';
import {PanelBankSectionsScrollContextProvider} from '../../components/PanelBank/PanelBankSectionsScrollContextProvider';
import {PanelBankWidthContextProvider} from '../../components/PanelBank/PanelBankWidthContext';
import {PanelsSharedRefsContextProvider} from '../../components/Views/contexts/PanelsSharedRefsContext';
import {NamedWorkspaceViewBarWrap} from '../../components/Views/NamedWorkspaceViewBar';
import {PanelsQuickAddContextProvider} from '../../components/WorkspaceDrawer/PanelsQuickAdd/PanelsQuickAddContext';
import {useWorkspaceSettingsContext} from '../../components/WorkspaceDrawer/Settings/WorkspaceSettingsContextProvider';
import {WorkspaceDrawer} from '../../components/WorkspaceDrawer/WorkspaceDrawer';
import {WorkspaceDrawerContextProvider} from '../../components/WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {WorkspaceModePrompt} from '../../components/WorkspaceModePrompt/WorkspaceModePrompt';
import {usePropsSelector} from '../../state/hooks';
import {PanelBankSectionsPagingContextProvider} from '../../state/panelbank/PanelBankSectionsPagingContext';
import {RunQueryContext} from '../../state/runs/context';
import {useNewObjectRef} from '../../state/views/hooks';
import * as WorkspaceSelectors from '../../state/workspaces/selectors';
import {color} from '../../util/colors';
import * as FilterUtil from '../../util/filters';
import * as QueryTypes from '../../util/queryTypes';
import * as RunFeedUtil from '../../util/runfeed';
import * as SMUtil from '../../util/selectionmanager';
import {RunPagePanels} from './RunPagePanels';
import {useRunWorkspaceRunContext} from './RunWorkspaceRunContext';
import {RUN_VIEW_TYPE, SingleRunWorkspaceAllProps} from './types';

const RunWorkspace = ({
  workspaceID,
  entityName,
  projectName,
  isProjectReadOnly,
}: SingleRunWorkspaceAllProps) => {
  const run = useRunWorkspaceRunContext();
  const [panelBankWidth, setPanelBankWidth] = useState(0);

  const {
    workspaceLayout: {shouldAutoGeneratePanels},
  } = useWorkspaceSettingsContext();

  const runPageDataResult = usePropsSelector(
    WorkspaceSelectors.makeRunWorkspaceSelector,
    workspaceID
  );
  const runPageData = WorkspaceSelectors.getDataChecked(runPageDataResult);
  const {isFullScreenPanelPage, isEmbeddedPanel} = useDetectPanelVariant();

  const context = useMemo(() => {
    return {
      entityName,
      projectName,
      runId: run?.name,
      runSetName: `Run: ${run?.displayName}`,
    };
  }, [entityName, projectName, run]);

  // Need to memoize this or useNewObjectRef will generate a new
  // object repeatedly
  const runSet = useMemo(() => {
    return {
      id: 'run-page-runset-id',
      name: 'run-runset',
      filters: FilterUtil.rootFilterSingle({
        key: {section: 'run', name: 'name'},
        op: '=',
        value: run?.name ?? '',
      }),
      grouping: [],
      selections: SMUtil.EMPTY_ALL_SELECTIONS,
      expandedRowAddresses: [],
      enabled: true,
      sort: {
        keys: [
          {
            key: {section: 'run', name: 'createdAt'},
            ascending: true,
          },
        ],
      } as QueryTypes.Sort,
      runFeed: RunFeedUtil.EMPTY_CONFIG,
      search: {query: ''},
      searchHistory: [],
      runNameTruncationType: TruncationType.Middle,
    };
  }, [run]);

  const runSetRefNew = useNewObjectRef('runSet', runSet);

  const runSetRefs = useMemo(() => {
    if (!runSetRefNew.ready || run == null) {
      return [];
    }
    return [runSetRefNew.ref];
  }, [runSetRefNew.ready, runSetRefNew.ref, run]);

  // Need to memoize this or useNewObjectRef will generate a new
  // object repeatedly
  const runColors = useMemo(() => {
    return {[run.name]: color(0)};
  }, [run.name]);
  const runColorsRefNew = useNewObjectRef('run-colors', runColors);

  if (run == null) {
    return (
      <div style={{margin: '20px'}}>
        Run data corrupted; ensure validity of associated logging script.
      </div>
    );
  }

  if (!runColorsRefNew.ready) {
    return <div />;
  }

  return (
    <RunQueryContext.Provider value={context}>
      <KeyInfoQueryContextProvider
        entityName={entityName}
        runSetRefs={runSetRefs}>
        <PanelsSharedRefsContextProvider
          customRunColorsRef={runColorsRefNew.ref}
          panelBankConfigRef={runPageData.panelBankConfigRef}
          runSetRefs={runSetRefs}
          workspaceSettingsRef={runPageData.workspaceSettingsRef}>
          <WorkspaceDrawerContextProvider panelBankWidth={panelBankWidth}>
            <PanelBankSectionsPagingContextProvider>
              {!isEmbeddedPanel && (
                <NamedWorkspaceViewBarWrap
                  workspaceStateId={workspaceID}
                  viewType={RUN_VIEW_TYPE}
                  isProjectReadOnly={isProjectReadOnly}
                />
              )}
              {shouldAutoGeneratePanels === 'pending' ? (
                <WorkspaceModePrompt />
              ) : (
                <PanelsQuickAddContextProvider>
                  <PanelBankWidthContextProvider
                    panelBankWidth={panelBankWidth}
                    setPanelBankWidth={setPanelBankWidth}>
                    <AutoSyncPanelBankContextProvider>
                      <PanelBankSectionsScrollContextProvider>
                        {isFullScreenPanelPage ? (
                          <FullPagePanel
                            isProjectReadOnly={isProjectReadOnly}
                          />
                        ) : isEmbeddedPanel ? (
                          <EmbeddablePanel
                            isProjectReadOnly={isProjectReadOnly}
                          />
                        ) : (
                          <div style={{display: 'flex', flexGrow: 1}}>
                            {runSetRefNew.ready && (
                              <RunPagePanels
                                entityName={entityName}
                                projectName={projectName}
                                isProjectReadOnly={isProjectReadOnly}
                                workspaceID={workspaceID}
                              />
                            )}
                            <WorkspaceDrawer />
                          </div>
                        )}
                      </PanelBankSectionsScrollContextProvider>
                    </AutoSyncPanelBankContextProvider>
                  </PanelBankWidthContextProvider>
                </PanelsQuickAddContextProvider>
              )}
            </PanelBankSectionsPagingContextProvider>
          </WorkspaceDrawerContextProvider>
        </PanelsSharedRefsContextProvider>
      </KeyInfoQueryContextProvider>
    </RunQueryContext.Provider>
  );
};

export default RunWorkspace;
